import type { Preferences } from '@cobliteam/common-types';
import type { PageDef } from '@cobliteam/dashboard/src/dashboardDef.type';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface VisitedPage {
  page: PageDef;
  count: number;
  lastVisited: Date;
}

const MAX_STORED_PAGES = 100; // Store more pages for tracking
const MAX_DISPLAYED_PAGES = 8; // Show only top 8 in menu

// Helper functions for localStorage
const getPreferencesFromStorage = (): Partial<Preferences> => {
  try {
    const stored = window.localStorage.getItem('preferences');
    if (!stored) return {};

    const parsed = JSON.parse(stored);

    // Convert lastVisited strings back to Date objects
    if (parsed.visitedPages) {
      parsed.visitedPages = parsed.visitedPages.map((visitedPage: any) => ({
        ...visitedPage,
        lastVisited: new Date(visitedPage.lastVisited),
      }));
    }

    return parsed;
  } catch {
    return {};
  }
};

const savePreferencesToStorage = (preferences: Partial<Preferences>): void => {
  try {
    window.localStorage.setItem('preferences', JSON.stringify(preferences));
  } catch {
    // Handle storage quota exceeded or other errors
  }
};

export const useVisitedPage = () => {
  const { sidebar } = useDashboard();
  const [preferences, setPreferences] = useState<Partial<Preferences>>(getPreferencesFromStorage);

  // Initialize visitedPages from localStorage if it doesn't exist
  useEffect(() => {
    const currentPrefs = getPreferencesFromStorage();
    if (!currentPrefs?.visitedPages) {
      const newPrefs = {
        ...currentPrefs,
        visitedPages: [],
      };
      savePreferencesToStorage(newPrefs);
      setPreferences(newPrefs);
    }
  }, []);

  // Memoize visitedPages to prevent unnecessary re-renders
  // Return top most visited pages sorted by visit count and recency
  // Only include pages that still exist in the current dashboard definition
  const visitedPages = useMemo(() => {
    const allVisitedPages = preferences?.visitedPages || [];
    const validPageIds = new Set(sidebar.pagesFlatted.map((p) => p.id));

    return allVisitedPages
      .filter((vp) => validPageIds.has(vp.page.id))
      .map((vp) => {
        // Always use the current page definition to reflect route changes
        const currentPage = sidebar.pagesFlatted.find((p) => p.id === vp.page.id);
        return currentPage ? { ...vp, page: currentPage } : vp;
      })
      .filter((vp) => !!vp.page.url)
      .sort((a, b) => {
        // First sort by count (higher count first)
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        // If same count, sort by last visited (more recent first)
        return new Date(b.lastVisited).getTime() - new Date(a.lastVisited).getTime();
      })
      .slice(0, MAX_DISPLAYED_PAGES);
  }, [preferences?.visitedPages, sidebar.pagesFlatted]);

  // Memoize trackPageVisit to prevent unnecessary re-renders in child components
  const trackPageVisit = useCallback(
    (pageId: string) => {
      const page = sidebar.pagesFlatted.find((page) => page.id === pageId && !!page.url && page.id !== 'home');
      if (!page) {
        return;
      }

      // Get current preferences from localStorage
      const currentPreferences = getPreferencesFromStorage();
      const currentVisitedPages = [...(currentPreferences?.visitedPages || [])];
      const existingPageIndex = currentVisitedPages.findIndex((stored) => stored?.page?.id === pageId);

      if (existingPageIndex === -1) {
        // Add new page
        currentVisitedPages.push({
          page,
          count: 1,
          lastVisited: new Date(),
        });
      } else {
        // Update existing page
        currentVisitedPages[existingPageIndex] = {
          ...currentVisitedPages[existingPageIndex],
          count: currentVisitedPages[existingPageIndex].count + 1,
          lastVisited: new Date(),
        };
      }

      // Keep only the most recent MAX_STORED_PAGES pages
      // Sort by last visited to keep the most recent ones
      const trimmedPages = currentVisitedPages.sort((a, b) => new Date(b.lastVisited).getTime() - new Date(a.lastVisited).getTime()).slice(0, MAX_STORED_PAGES);

      const newPreferences = {
        ...currentPreferences,
        visitedPages: trimmedPages,
      };

      // Save to localStorage and update state
      savePreferencesToStorage(newPreferences);
      setPreferences(newPreferences);
    },
    [sidebar.pagesFlatted]
  );

  // Helper function to clear all visited pages
  const clearVisitedPages = useCallback(() => {
    const currentPreferences = getPreferencesFromStorage();
    const newPreferences = {
      ...currentPreferences,
      visitedPages: [],
    };
    savePreferencesToStorage(newPreferences);
    setPreferences(newPreferences);
  }, []);

  return {
    visitedPages,
    trackPageVisit,
    clearVisitedPages,
  };
};
